import React, {useMemo, useState} from 'react';

export interface FlagIconProps {
  countryCode?: string;
  size?: number;
}

// Pinned to 1.5.21: this is the latest version available on the parastorage CDN that still
// ships the flag SVGs (newer versions, e.g. 1.6.x, are missing those files on the CDN).
// Using the 1x1 (square) variant so flags sit centered inside the round container without
// the side-cropping that the 3x2 ratio causes.
const COUNTRY_FLAG_ICONS_BASE_URL = 'https://static.parastorage.com/unpkg/country-flag-icons@1.5.21/flags/1x1/';
const CODES_WITHOUT_CDN_FLAG = new Set(['UN']);

const normalizeCountryCode = (countryCode?: string) => {
  const code = countryCode?.toUpperCase();

  return code && /^[A-Z]{2}$/.test(code) ? code : undefined;
};

const countryCodeToFlagEmoji = (countryCode: string) =>
  countryCode.replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)));

export const FlagIcon = ({countryCode, size = 24}: FlagIconProps) => {
  const iconCode = normalizeCountryCode(countryCode);
  const [failedIconCode, setFailedIconCode] = useState<string | undefined>();
  const fallbackFlagEmoji = useMemo(() => (iconCode ? countryCodeToFlagEmoji(iconCode) : undefined), [iconCode]);
  const shouldUseFallback = !iconCode || CODES_WITHOUT_CDN_FLAG.has(iconCode) || failedIconCode === iconCode;

  if (!iconCode) {
    return null;
  }

  return (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        minWidth: size,
        borderRadius: '50%',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        flex: '0 0 auto',
      }}>
      {shouldUseFallback ? (
        <span
          style={{
            fontSize: Math.round(size * 0.78),
            lineHeight: `${size}px`,
            textAlign: 'center',
            width: '100%',
            height: '100%',
          }}>
          {fallbackFlagEmoji}
        </span>
      ) : (
        <img
          src={`${COUNTRY_FLAG_ICONS_BASE_URL}${iconCode}.svg`}
          alt=""
          onError={() => setFailedIconCode(iconCode)}
          style={{
            width: '100%',
            height: '100%',
            display: 'block',
            objectFit: 'cover',
            objectPosition: 'center',
          }}
        />
      )}
    </span>
  );
};
